// Entry paths (Frontend §4.1): one index.html; the path picks the mode.

/** API.md §3.1 `ClinicSlug`: 3–40 characters, lowercase letters, digits and inner hyphens. */
export const CLINIC_SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])$/

export type AppMode =
  | { kind: 'direct'; slug: string }
  /** `slug: null`: an embed path with a malformed slug (the frame shows "This booking page doesn't exist"). */
  | { kind: 'embed'; slug: string | null }
  | { kind: 'notFound' }

/**
 * Slug resolution (Architecture §5, Frontend §4.1): `/c/{slug}/embed` (the iframe embed.js opens, slug from
 * `data-clinic`), a `/c/{slug}` path, else `/` with `VITE_DEFAULT_CLINIC_SLUG`. A malformed slug, any other path, or
 * `/` without a default → the "This booking page doesn't exist" page.
 */
export function resolveMode(pathname: string, defaultSlug: string | undefined): AppMode {
  if (pathname === '/' || pathname === '') {
    return defaultSlug !== undefined && CLINIC_SLUG_RE.test(defaultSlug)
      ? { kind: 'direct', slug: defaultSlug }
      : { kind: 'notFound' }
  }
  const match = /^\/c\/([^/]+)(\/embed)?\/?$/.exec(pathname)
  const slug = match?.[1]
  if (slug === undefined) return { kind: 'notFound' }
  if (match?.[2]) return { kind: 'embed', slug: CLINIC_SLUG_RE.test(slug) ? slug : null }
  return CLINIC_SLUG_RE.test(slug) ? { kind: 'direct', slug } : { kind: 'notFound' }
}

/** The direct link an embed path opened top-level redirects to (Frontend §4.1): `/c/{slug}`, keeping `?src=`. */
export function directLinkFor(slug: string, search: string): string {
  const src = new URLSearchParams(search).get('src')
  return `/c/${slug}${src !== null && src !== '' ? `?src=${encodeURIComponent(src)}` : ''}`
}
